import { Icon } from "@sellar-io/components";
import type { ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { useNavigationOverlayPresentation } from "./NavigationOverlayContext.shared";

export const NavigationOverlayHeader = ({
  title,
  count,
  countClassName,
  action,
  onBack,
  onClose,
  autoFocusBack = false,
}: {
  title: string;
  count?: number | string;
  countClassName?: string;
  action?: ReactNode;
  onBack?: () => void;
  onClose?: () => void;
  autoFocusBack?: boolean;
}) => {
  const isPanel = useNavigationOverlayPresentation() === "panel";
  const controlClasses = twMerge(
    "inline-flex shrink-0 items-center justify-center rounded-md hover:bg-zinc-100 focus-visible:outline-purple-600",
    isPanel ? "size-11" : "size-6",
  );
  return (
    <div
      className={twMerge(
        "flex shrink-0 items-center",
        isPanel ? "h-14 gap-2 border-b border-zinc-200 px-4" : "gap-1 pb-2",
      )}
    >
      {onBack && (
        <button
          type="button"
          aria-label="Back"
          autoFocus={autoFocusBack}
          onClick={onBack}
          className={controlClasses}
        >
          <Icon icon="CHEVRON_LEFT" size="20px" />
        </button>
      )}
      <h2
        className={twMerge(
          "flex min-w-0 flex-1 items-center gap-1 text-base font-semibold",
          isPanel ? "leading-6" : "leading-[23px]",
        )}
      >
        {title}
        {count !== undefined && (
          <span
            className={twMerge(
              "inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-purple-600 px-1 text-xs leading-[18px] text-white",
              countClassName,
            )}
          >
            {count}
          </span>
        )}
      </h2>
      {action && (
        <div className="text-xs leading-[17px] font-medium text-purple-700">
          {action}
        </div>
      )}
      {onClose && (
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className={controlClasses}
        >
          <Icon icon="CLOSE" size="20px" />
        </button>
      )}
    </div>
  );
};

export const NavigationOverlayBody = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => {
  const isPanel = useNavigationOverlayPresentation() === "panel";
  return (
    <div
      className={twMerge(
        "flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain",
        isPanel ? "p-4 pb-8" : "gap-2",
        className,
      )}
    >
      {children}
    </div>
  );
};

export const NavigationOverlayGroup = ({
  title,
  children,
}: {
  title?: string;
  children: ReactNode;
}) => {
  const isPanel = useNavigationOverlayPresentation() === "panel";
  return (
    <div
      className={twMerge(
        "shrink-0",
        isPanel && (title ? "[&:first-child>h3]:pt-0" : "mt-2 first:mt-0"),
      )}
    >
      {title && (
        <h3
          className={twMerge(
            "text-sm leading-5 font-semibold text-zinc-500",
            isPanel ? "pt-4 pb-2" : "mb-2",
          )}
        >
          {title}
        </h3>
      )}
      <div
        className={twMerge(
          "flex flex-col",
          isPanel
            ? "divide-y divide-zinc-200 overflow-hidden rounded-2xl border border-zinc-200 shadow-[2px_2px_16px_0_#0000000f]"
            : "gap-2",
        )}
      >
        {children}
      </div>
    </div>
  );
};

export const NavigationOverlayFooter = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => {
  const isPanel = useNavigationOverlayPresentation() === "panel";
  return (
    <div
      className={twMerge(
        "shrink-0 border-t border-zinc-200",
        isPanel ? "p-4" : "pt-3",
        className,
      )}
    >
      {children}
    </div>
  );
};
